/*
  Volume discount ladder ("Buy more, save more"), drawn by the theme from
  Appstle Bundles' own rules in place of Appstle's widget, which cost phones
  several seconds of work on every product page. Rendered by
  snippets/nr-offer-widget.liquid inside the product form.

  - Reads the active VOLUME_DISCOUNT rule for this variant, so tiers, labels,
    photos, badges and free gifts are still set up in Appstle. It draws first
    from the rules printed next to it (from Appstle's shop metafield), as soon
    as the buy box arrives, then checks them against _ABConfig (printed by
    the Appstle Bundles app embed at the end of the page) and redraws if they
    differ. Loaded async, so it never waits for the rest of the page.
  - Prices follow Appstle's discount function: a tier costs the variant price
    x quantity, less its percentage, rounded to the penny. The crossed-out
    price is the full price of the packs plus any free gifts.
  - The chosen tier goes into the product form as the quantity, Appstle's
    line properties (so its discount function applies the right rule) and,
    for a free-gift tier, one extra line per gift, so Dawn's own Add to Cart
    adds everything in one request.
  - Where the rule says so, the price block shows the tier's price, "Was" and
    the saving (the section's Saving wording).
  - If the rule uses something this ladder doesn't draw (tier restrictions,
    upsells, mix and match, customer tags), Appstle's own widget is loaded
    instead, as before.
*/
if (!customElements.get('nr-offer-ladder')) {
  const parse = (value, fallback) => {
    if (value && typeof value === 'object') return value;
    try {
      return JSON.parse(value) ?? fallback;
    } catch (error) {
      return fallback;
    }
  };

  const escape = (value) =>
    String(value ?? '').replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
    );

  // "BUY MORE, SAVE MORE" reads "Buy more, save more", as the old widget did.
  const sentence = (value) => {
    const text = String(value ?? '')
      .trim()
      .toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const sized = (src, width) => {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (error) {
      return src;
    }
  };

  const activeNow = (rule) => {
    const now = Date.now();
    const from = rule.bundleActiveFrom ? Date.parse(rule.bundleActiveFrom) : null;
    const to = rule.bundleActiveTo ? Date.parse(rule.bundleActiveTo) : null;
    return !(from && now < from) && !(to && now > to);
  };

  // Runs fn once the whole page is in (Dawn's scripts, _ABConfig, any
  // quantity selector below the ladder), or now if it already is.
  const whenParsed = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  };

  // Free gift prices in pence by variant id, from the Storefront API as
  // Appstle reads them (a gift's handle in the rule can be out of date).
  const giftPrices = (ids) => {
    const config = window._ABConfig || {};
    const headers = { 'Content-Type': 'application/json' };
    if (config.assetRev) headers['X-Shopify-Storefront-Access-Token'] = config.assetRev;
    return fetch(`/api/${config.storeFrontApiVersion || '2026-01'}/graphql.json`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: 'query Gifts($ids: [ID!]!) { nodes(ids: $ids) { ... on ProductVariant { id price { amount } } } }',
        variables: { ids: ids.map((id) => `gid://shopify/ProductVariant/${id}`) },
      }),
    })
      .then((response) => (response.ok ? response.json() : null))
      .then(
        (result) =>
          new Map(
            (result?.data?.nodes || [])
              .filter((node) => node?.price)
              .map((node) => [node.id.split('/').pop(), Math.round(parseFloat(node.price.amount) * 100)]),
          ),
      )
      .catch(() => new Map());
  };

  customElements.define(
    'nr-offer-ladder',
    class NrOfferLadder extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          const tier = event.target.closest('.nr-ladder__tier');
          if (tier) this.select(Number(tier.dataset.index), false);
        });
        this.addEventListener('keydown', (event) => this.onKey(event));
      }

      connectedCallback() {
        if (this.started) return;
        this.started = true;
        this.form = this.closest('form');
        this.variantId = this.dataset.variantId;
        this.price = Number(this.dataset.price);
        this.load = this.load.bind(this);

        if (!this.form) {
          this.finish('empty');
          return;
        }

        // The rules printed beside the ladder: draw from them now.
        const printed = document.querySelector(`script[data-nr-ladder-rules="${this.dataset.sectionId}"]`);
        this.printedRules = printed ? parse(printed.textContent, null) : null;
        if (Array.isArray(this.printedRules) && this.printedRules.length) this.load();

        whenParsed(() => {
          if (!this.started) return;
          // Then from Appstle's own copy, which wins if they differ.
          const config = window._ABConfig;
          if (config) {
            if (typeof config.whenConfigReady === 'function') config.whenConfigReady(this.load);
            else this.load();
            document.addEventListener('appstle-bundles:rules-loaded', this.load);
          } else if (this.dataset.state === 'pending') {
            this.finish('empty');
          }
          // A quantity selector further down the page now exists.
          if (this.rule) this.update();

          // Dawn redraws the price block when the variant changes.
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && !this.unsubscribe) {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              if (event.data?.sectionId !== this.dataset.sectionId || !event.data.variant) return;
              this.variantId = String(event.data.variant.id);
              this.price = Number(event.data.variant.price);
              this.selected = null;
              this.load();
            });
          }
        });
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        this.unsubscribe = null;
        document.removeEventListener('appstle-bundles:rules-loaded', this.load);
        this.started = false;
      }

      load() {
        const config = window._ABConfig;
        const fromConfig = Array.isArray(config?.bundle_rules);
        const rules = fromConfig ? config.bundle_rules : this.printedRules;
        if (!Array.isArray(rules)) {
          if (config) this.fallback();
          return;
        }

        const rule = rules.find(
          (item) =>
            item?.bundleType === 'VOLUME_DISCOUNT' &&
            item.status === 'ACTIVE' &&
            activeNow(item) &&
            parse(item.variants, []).some((variant) => String(variant.variantId) === String(this.variantId)),
        );

        if (!rule) {
          // Wait for Appstle's copy before saying there is no offer.
          if (!fromConfig) return;
          // Appstle can print only some rules and fetch the rest: look again
          // once they are in (or failed to come).
          if (config.bundleRulesPartial && typeof config.loadAllBundleRules === 'function' && !this.loadingAll) {
            this.loadingAll = true;
            Promise.resolve(config.loadAllBundleRules()).then(this.load, this.load);
            return;
          }
          this.finish('empty');
          return;
        }

        const tiers = parse(rule.tieredDiscount, []);
        const settings = parse(rule.settings, {});
        if (!this.drawable(rule, tiers, settings)) {
          // Hand over only once Appstle's copy agrees and its script is in.
          if (fromConfig) this.fallback();
          return;
        }

        // Appstle's copy matches what is drawn already: leave it as it is.
        const key = [this.variantId, this.price, rule.uniqueRef, rule.name, rule.tieredDiscount, rule.settings]
          .map((value) => (typeof value === 'string' ? value : JSON.stringify(value)))
          .join('|');
        if (key === this.ruleKey && this.dataset.state === 'ready') return;
        this.ruleKey = key;

        this.rule = rule;
        this.tiers = tiers;
        this.settings = settings;
        if (this.selected == null || !tiers[this.selected]) {
          this.selected = Math.max(
            0,
            tiers.findIndex((tier) => tier.selectByDefault),
          );
        }
        this.render();
        this.loadGifts();
      }

      // Only what this ladder can draw and add exactly as Appstle would.
      drawable(rule, tiers, settings) {
        if (!Array.isArray(tiers) || !tiers.length) return false;
        if (rule.customerIncludeTags || rule.allowedTags || rule.restrictTags || rule.limitToUsePerCustomer > 0)
          return false;
        if (
          rule.productSelectionType &&
          rule.productSelectionType !== 'VARIANT' &&
          rule.productSelectionType !== 'PRODUCT'
        )
          return false;
        if (settings.enableVolumeDiscountTierRestriction || settings.enableVolumeDiscountMixAndMatch) return false;
        if (settings.enableTierSellingPlan || settings.applyDiscountOnlyIfAddedFromWidget) return false;
        return tiers.every(
          (tier) =>
            tier.discountBasedOn === 'QUANTITY' &&
            tier.discountType === 'PERCENTAGE' &&
            Number.isInteger(Number(tier.value)) &&
            Number(tier.value) > 0 &&
            Number.isFinite(Number(tier.discount ?? 0)) &&
            !(tier.upsells || []).length,
        );
      }

      money(cents) {
        const format = this.dataset.moneyFormat || '£{{amount}}';
        const value = (Math.round(cents) / 100).toFixed(2);
        const [whole, pennies] = value.split('.');
        const grouped = (separator) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, kind) => {
          if (kind === 'amount_no_decimals') return grouped(',');
          if (kind === 'amount_with_comma_separator') return `${grouped('.')},${pennies}`;
          if (kind === 'amount_no_decimals_with_comma_separator') return grouped('.');
          return `${grouped(',')}.${pennies}`;
        });
      }

      // The tier's price, its full price and the saving, in pence.
      prices(tier, index) {
        const quantity = Number(tier.value);
        const full = this.price * quantity;
        const total = Math.round(full * (1 - Number(tier.discount || 0) / 100));
        let original = full;
        (tier.freeProducts || []).forEach((gift) => {
          const price = this.gifts?.get(String(gift.variantId));
          if (price) original += price * (gift.quantity || 1);
        });
        // The section's "Crossed-out price on tier 2" setting.
        const override = this.dataset.tier2ComparePrice;
        if (index === 1 && override) {
          const pence = Math.round(parseFloat(override.replace(/[^\d.]/g, '')) * 100);
          if (pence > 0) original = pence;
        }
        return { quantity, total, original, saved: Math.max(0, original - total) };
      }

      render() {
        const name = sentence(this.rule.name);
        const fallbackImage = parse(this.rule.variants, []).find(
          (variant) => String(variant.variantId) === String(this.variantId),
        )?.imageSrc;

        const tiers = this.tiers
          .map((tier, index) => {
            const price = this.prices(tier, index);
            const selected = index === this.selected;
            const image = tier.imageURL || fallbackImage;
            const save = String(tier.saveBadgeLabel || '').replace(
              /\{\{\s*saved_amount\s*\}\}/g,
              this.money(price.saved),
            );
            const gifts = (tier.freeProducts || [])
              .map((gift) => {
                const value = this.gifts?.get(String(gift.variantId));
                return `<span class="nr-ladder__gift">
                  ${gift.imageSrc ? `<img class="nr-ladder__gift-image" src="${escape(sized(gift.imageSrc, 64))}" alt="" width="28" height="28" loading="lazy">` : ''}
                  <span class="nr-ladder__gift-label">${escape(gift.label || gift.name)}</span>
                  ${value && gift.showOriginalPrice !== false ? `<s class="nr-ladder__gift-price">${escape(this.money(value * (gift.quantity || 1)))}</s>` : ''}
                </span>`;
              })
              .join('');

            return `<button type="button" role="radio" class="nr-ladder__tier${selected ? ' is-selected' : ''}${tier.badgeLabel ? ' nr-ladder__tier--badge' : ''}"
                aria-checked="${selected}" tabindex="${selected ? 0 : -1}" data-index="${index}">
                ${tier.badgeLabel ? `<span class="nr-ladder__badge">${escape(sentence(tier.badgeLabel))}</span>` : ''}
                <span class="nr-ladder__row">
                  <span class="nr-ladder__radio" aria-hidden="true"></span>
                  ${image ? `<img class="nr-ladder__image" src="${escape(sized(image, 112))}" alt="" width="56" height="56" loading="lazy">` : '<span></span>'}
                  <span class="nr-ladder__name">
                    <span class="nr-ladder__title">${escape(tier.titleLabel)}</span>
                    ${tier.subtitleLabel ? `<span class="nr-ladder__line">${escape(tier.subtitleLabel)}</span>` : ''}
                    ${save.trim() ? `<span class="nr-ladder__line">${escape(save)}</span>` : ''}
                  </span>
                  <span class="nr-ladder__prices">
                    <span class="nr-ladder__price">${escape(this.money(price.total))}</span>
                    ${price.original > price.total ? `<s class="nr-ladder__was">${escape(this.money(price.original))}</s>` : ''}
                  </span>
                </span>
                ${gifts ? `<span class="nr-ladder__gifts">${gifts}</span>` : ''}
              </button>`;
          })
          .join('');

        this.innerHTML = `<p class="nr-ladder__heading" id="${escape(this.dataset.sectionId)}-ladder">${escape(name)}</p>
          <div class="nr-ladder__tiers" role="radiogroup" aria-labelledby="${escape(this.dataset.sectionId)}-ladder">${tiers}</div>
          <span class="nr-ladder__fields" hidden></span>`;
        this.update();
        this.finish('ready');
      }

      // Gift prices for the crossed-out prices; drawn again once they are in.
      loadGifts() {
        const ids = [
          ...new Set(this.tiers.flatMap((tier) => tier.freeProducts || []).map((gift) => String(gift.variantId))),
        ];
        const key = ids.join();
        if (!ids.length || this.giftsKey === key) return;
        this.giftsKey = key;
        giftPrices(ids).then((prices) => {
          this.gifts = prices;
          if (prices.size && this.isConnected && this.rule) this.render();
        });
      }

      select(index, focus) {
        if (!this.tiers?.[index]) return;
        this.selected = index;
        this.querySelectorAll('.nr-ladder__tier').forEach((tier) => {
          const on = Number(tier.dataset.index) === index;
          tier.classList.toggle('is-selected', on);
          tier.setAttribute('aria-checked', on);
          tier.tabIndex = on ? 0 : -1;
          if (on && focus) tier.focus();
        });
        this.update();
      }

      onKey(event) {
        if (!event.target.closest('.nr-ladder__tier')) return;
        const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        this.select((this.selected + step + this.tiers.length) % this.tiers.length, true);
      }

      // The form fields for the chosen tier, the price block, and a change event
      // for the Add to Cart price and the sticky bar.
      update() {
        const tier = this.tiers[this.selected];
        const price = this.prices(tier, this.selected);
        const bundle = {
          '_appstle-bb-id': this.rule.uniqueRef,
          '__appstle-bb-name': this.rule.name,
          _appstle_bundles_type: 'VOLUME_DISCOUNT',
        };

        const fields = [];
        const field = (name, value) =>
          fields.push(`<input type="hidden" name="${escape(name)}" value="${escape(value)}">`);

        // A quantity selector on the page takes the tier's quantity; otherwise
        // the ladder sends its own.
        const quantity = [...this.form.querySelectorAll('[name="quantity"]')].find((input) => !this.contains(input));
        if (quantity) quantity.value = price.quantity;
        else field('quantity', price.quantity);
        Object.entries(bundle).forEach(([key, value]) => field(`properties[${key}]`, value));
        (tier.freeProducts || []).forEach((gift, n) => {
          field(`items[${n}][id]`, gift.variantId);
          field(`items[${n}][quantity]`, gift.quantity || 1);
          Object.entries(bundle).forEach(([key, value]) => field(`items[${n}][properties][${key}]`, value));
          field(`items[${n}][properties][_appstle_bundle_combo_id]`, 'free_gift');
        });
        const box = this.querySelector('.nr-ladder__fields');
        if (box) box.innerHTML = fields.join('');

        // The section's "Price shows the chosen tier's price" setting asks for it too,
        // for rules saved without Appstle's own "update theme price" option.
        if (this.settings.updateThemeProductPrice || this.hasAttribute('data-offer-price')) this.updatePriceBlock(price);

        this.dispatchEvent(
          new CustomEvent('nr:offer-change', { bubbles: true, detail: { price: this.money(price.total) } }),
        );
      }

      updatePriceBlock(price) {
        const block = document.getElementById(`price-${this.dataset.sectionId}`);
        if (!block) return;
        const savings = (this.dataset.savingsText || 'You save [amount]').replace('[amount]', this.money(price.saved));
        block.querySelectorAll('.price__container .price-item--regular').forEach((item) => {
          item.textContent = this.money(price.total);
          item.setAttribute('data-nr-offer-price', '');
          let details = item.nextElementSibling;
          if (!details?.classList.contains('nr-offer-price')) {
            details = document.createElement('span');
            details.className = 'nr-offer-price';
            item.after(details);
          }
          details.innerHTML =
            this.settings.showThemeProductPriceSavings !== false && price.original > price.total
              ? `<s class="nr-offer-price__was">${escape(this.money(price.original))}</s><span class="nr-offer-price__saving">${escape(savings)}</span>`
              : '';
        });
      }

      finish(state) {
        if (state === 'empty') this.innerHTML = '';
        this.dataset.state = state;
      }

      // Appstle's own widget, styled as before, for a rule this ladder can't draw.
      fallback() {
        if (this.dataset.state === 'appstle') return;
        this.innerHTML = '';
        this.id = 'appstleVolumeDiscountContainer';
        this.classList.add('ab-volume-discount-container');
        const css = this.dataset.appstleCss;
        if (css && !document.querySelector(`link[href="${css}"]`)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = css;
          document.head.appendChild(link);
        }
        this.dataset.state = 'appstle';
        // Before DOMContentLoaded, Appstle's loader finds the container itself.
        if (document.readyState !== 'loading') window.initVolumeDiscountAppstleBundle?.();
      }
    },
  );
}
